/*
 * 左下角存储常驻入口（webStorageEntrance AB 实验组）
 * 4 态：未超限 / 接近超限(≥90%) / 已超限未受限(≥100%) / 已超限已受限(viewLimited)
 *
 * 数据流：复用 useEffectiveStorage hook
 * 点击：打开 VipPaymentModal，sourceDetail='leftBottomStorageEntrance'
 *       03 需求会在此处挂一个 variant 替换弹窗顶部标题/副标题
 */
import React, { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import {
  useAbFlag,
  useAppDispatch,
  useAppSelector,
  useEffectiveStorage,
  useVip,
  useVipKind
} from '../../app/hooks'
import { setPaymentVisible } from '../../actions/main'
import sensors from '../../utils/sensors'
import storageIcon from './images/storage_entrance_icon.svg?url'
import { storageEntranceCustomContent } from './storageEntranceVariant'
import CONFIG from '../../constants/config'

// 无推荐等级时的「升到上一级」映射 · 与 Header.openPayment 的 kindMap 保持一致
const KIND_UPGRADE_MAP: Record<number, number> = {
  [CONFIG.VIP_KIND.VIP]: CONFIG.VIP_KIND.VVIP,
  [CONFIG.VIP_KIND.VVIP]: CONFIG.VIP_KIND.SVIP,
  [CONFIG.VIP_KIND.SVIP]: CONFIG.VIP_KIND.EVIP,
  [CONFIG.VIP_KIND.EVIP]: CONFIG.VIP_KIND.EVIP
}

import './StorageEntranceCard.scss'

type Stage = 'normal' | 'near' | 'over_safe' | 'over_limited'

const STORAGE_NEAR_RATIO = 0.9

interface DisplayConfig {
  stage: Stage
  mainText: string
  /** 是否粗体（超限态用粗体） */
  bold: boolean
}

// 张数格式化 · -1 为后端「无限存储」哨兵值 → 显示「无限」；≥ 1 万显示「X 万张」，否则千分位
const formatPhotoNum = (n: number): string => {
  if (n < 0) return '无限'
  if (n >= 10000) {
    const w = n / 10000
    return `${Number.isInteger(w) ? w : w.toFixed(1)}万张`
  }
  return n.toLocaleString('en-US') + ' 张'
}

interface Props {
  /** sidebar 折叠态 · true 时不渲染卡片（PRD 没出折叠态稿） */
  collapsed?: boolean
}

// 等级标签 · 与 hover 卡保持一致
const labelForKind = (vipKind: number): string => {
  if (vipKind === CONFIG.VIP_KIND.SVIP) return '超级会员'
  if (vipKind === CONFIG.VIP_KIND.VVIP) return '高级会员'
  if (vipKind === CONFIG.VIP_KIND.VIP) return '基础会员'
  return ''
}

const StorageEntranceCard: React.FC<Props> = ({ collapsed = false }) => {
  const enabled = useAbFlag('webStorageEntrance')
  const dispatch = useAppDispatch()
  const group = useAppSelector((state) => state.group)
  const { isVip } = useVip()
  const vipKind = useVipKind()
  const { totalNum, limitPhotoNum, viewLimited } = useEffectiveStorage()

  const ratio = limitPhotoNum > 0 ? totalNum / limitPhotoNum : 0

  // 命中且非折叠态时，给 body 加 class 让 brand-banner-fixed 隐藏（位置由本卡占据）
  const shouldOccupyBottom = enabled && !collapsed
  useEffect(() => {
    const cls = 'has-storage-entrance'
    if (shouldOccupyBottom) {
      document.body.classList.add(cls)
    } else {
      document.body.classList.remove(cls)
    }
    return () => {
      document.body.classList.remove(cls)
    }
  }, [shouldOccupyBottom])

  const display: DisplayConfig = useMemo(() => {
    if (ratio < STORAGE_NEAR_RATIO) {
      return { stage: 'normal', mainText: '存储空间', bold: false }
    }
    if (ratio < 1) {
      const remain = Math.max(1, Math.ceil((1 - ratio) * 100))
      return {
        stage: 'near',
        mainText: `存储仅剩 ${remain}%`,
        bold: false
      }
    }
    // ≥ 100%
    if (!isVip && viewLimited) {
      return {
        stage: 'over_limited',
        mainText: '存储已满，超限照片无法查看',
        bold: true
      }
    }
    return {
      stage: 'over_safe',
      mainText: '存储已满，超限照片将无法查看',
      bold: true
    }
  }, [ratio, viewLimited, isVip])

  const handleClick = () => {
    // 埋点：左下角存储入口点击
    sensors.track('vip_b4360_c7987_click', {
      clickItem: '左下角存储入口',
      groupID: group.groupId,
      track_sign: 'avip.b4360.c7987.click.vip_b4360_c7987_click'
    })
    // 推荐等级 · 与顶部入口 Header.newOnPay(false)/openPayment 完全一致，避免存储未满时恒跳超级会员：
    //  1) 临期会员(≤30天/已过期) → 续费当前等级
    //  2) 否则取后端推荐等级 topNeedVipKind
    //  3) 无推荐等级：非会员 → 基础会员；会员 → 当前等级上一级
    const statistics: any = group.statistics
    const stat = statistics?.data ?? statistics ?? {}
    const expireUnix = isVip ? group.vipInfo?.expire_time || 0 : 0
    const expireDays = expireUnix
      ? dayjs.unix(expireUnix).diff(dayjs(), 'days')
      : 0
    let recommendedKind: number
    if (isVip && expireDays <= 30) {
      recommendedKind = vipKind || CONFIG.VIP_KIND.SVIP
    } else if (stat.topNeedVipKind) {
      recommendedKind = stat.topNeedVipKind
    } else if (!isVip) {
      recommendedKind = CONFIG.VIP_KIND.VIP
    } else {
      recommendedKind = KIND_UPGRADE_MAP[vipKind] || CONFIG.VIP_KIND.SVIP
    }

    // 03 弹窗 variant · 全 4 态都替换标题/副标题
    const remainPercent =
      display.stage === 'near'
        ? Math.max(1, Math.ceil((1 - ratio) * 100))
        : undefined
    const customContent = storageEntranceCustomContent({
      stage: display.stage,
      isMember: !!isVip,
      totalNum,
      limitPhotoNum,
      remainPercent,
      levelName: labelForKind(vipKind)
    })

    dispatch(
      setPaymentVisible(
        true,
        'leftBottomStorageEntrance',
        recommendedKind,
        false,
        false,
        false,
        customContent as any
      )
    )
  }

  if (!enabled) return null
  if (collapsed) return null

  return (
    <div className="sider-storage-entrance">
      <div
        className={`storage-entrance-card storage-entrance-card--${display.stage}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
      >
        <div className="storage-entrance-card__head">
          <img
            src={storageIcon}
            alt=""
            className="storage-entrance-card__icon"
            draggable={false}
          />
          <span
            className={`storage-entrance-card__label${
              display.bold ? ' is-bold' : ''
            }`}
          >
            {display.mainText}
          </span>
        </div>
        <div className="storage-entrance-card__bar">
          <div
            className="storage-entrance-card__bar-fill"
            style={{ width: `${Math.min(100, ratio * 100)}%` }}
          />
        </div>
        <div className="storage-entrance-card__usage">
          <strong>{totalNum.toLocaleString('en-US')}</strong>
          {/* -1 为无限存储：只显示当前张数，不展示「/ 上限」 */}
          {limitPhotoNum < 0 ? (
            <span> 张</span>
          ) : (
            <>
              <span> / </span>
              <span className="storage-entrance-card__usage-limit">
                {formatPhotoNum(limitPhotoNum)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default StorageEntranceCard
